import { capacityStatsForState, type MovementType } from "@dailywars/core/engine";
import { Link } from "@tanstack/react-router";
import type { RefObject } from "react";

import { CoinMoneyIcon } from "#/components/game/CoinMoneyIcon";
import {
  gameDockedPanelFooterClassName,
  gameDockedPanelSurfaceClassName,
  gameDockedPanelTitleClassName,
  GameDockedPanelShell,
} from "#/components/game/GameDockedPanelShell";
import { MovementHudIcon } from "#/components/game/MovementHudIcon";
import { Button } from "#/components/ui/button";
import { useGameSelector } from "#/game/GameStoreProvider";

import { TUTORIAL_MAP } from "./tutorialGame";
import type { TutorialCapacityDisplay, TutorialPrompt } from "./tutorialStepTypes";
import { useTileScreenRect, type ScreenRect } from "./useTileScreenRect";

function capacityTypesToShow(display: TutorialCapacityDisplay): MovementType[] {
  if (display === "all") return ["foot", "vehicle"];
  return [display];
}

function CoachCapacityStat({ movementType }: { movementType: MovementType }) {
  const localState = useGameSelector((s) => s.localState);
  const { current, cap } = capacityStatsForState(localState).p1[movementType];

  return (
    <div className="flex items-center justify-center gap-2 rounded-lg bg-muted/60 px-3 py-2">
      <MovementHudIcon movementType={movementType} owner="p1" className="h-5 w-5" />
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {current}/{cap}
      </span>
    </div>
  );
}

function CoachCapacityStats({ display }: { display: TutorialCapacityDisplay }) {
  const types = capacityTypesToShow(display);
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {types.map((movementType) => (
        <CoachCapacityStat key={movementType} movementType={movementType} />
      ))}
    </div>
  );
}

function CoachIncomeStat() {
  const localState = useGameSelector((s) => s.localState);
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg bg-muted/60 px-3 py-2">
      <CoinMoneyIcon size="sm" />
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {localState.funds.p1}
      </span>
    </div>
  );
}

function CoachPanelBody({ prompt }: { prompt: TutorialPrompt }) {
  return (
    <>
      <p className={gameDockedPanelTitleClassName}>{prompt.title}</p>
      <p className="text-sm leading-relaxed text-muted-foreground">{prompt.body}</p>
      {prompt.showCapacity ? <CoachCapacityStats display={prompt.showCapacity} /> : null}
      {prompt.showIncome ? <CoachIncomeStat /> : null}
    </>
  );
}

function TileHighlight({ rect }: { rect: ScreenRect }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed z-40 rounded-md ring-4 ring-amber-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.04)] animate-pulse"
      style={{
        left: rect.left + 2,
        top: rect.top + 2,
        width: rect.width - 4,
        height: rect.height - 4,
      }}
    />
  );
}

function TutorialCoachPanel({
  prompt,
  isFinal,
  bottomDockRef,
  onRestart,
  onDismiss,
}: {
  prompt: TutorialPrompt;
  isFinal: boolean;
  bottomDockRef: RefObject<HTMLElement | null>;
  onRestart: () => void;
  onDismiss: () => void;
}) {
  const modal = Boolean(prompt.dismissible || isFinal);
  const onCancel = prompt.dismissible ? onDismiss : () => {};

  return (
    <GameDockedPanelShell
      bottomDockRef={bottomDockRef}
      onCancel={onCancel}
      closeAriaLabel={prompt.dismissible ? "Close tutorial tip" : "Close panel"}
      modal={modal}
    >
      <div className={gameDockedPanelSurfaceClassName}>
        <CoachPanelBody prompt={prompt} />
        {prompt.dismissible ? (
          <div className={gameDockedPanelFooterClassName}>
            <Button onClick={onDismiss} size="lg">
              Got it
            </Button>
          </div>
        ) : null}
        {isFinal ? (
          <div className={gameDockedPanelFooterClassName}>
            <Button onClick={onRestart} size="lg" variant="outline">
              Restart tutorial
            </Button>
            <Button render={<Link to="/" preload={false} />} size="lg">
              Back to home
            </Button>
          </div>
        ) : null}
      </div>
    </GameDockedPanelShell>
  );
}

export function TutorialCoach({
  prompt,
  isFinal,
  bottomDockRef,
  onRestart,
  onDismiss,
}: {
  prompt: TutorialPrompt;
  isFinal: boolean;
  bottomDockRef: RefObject<HTMLElement | null>;
  onRestart: () => void;
  onDismiss: () => void;
}) {
  const tileCoord = prompt.highlight?.kind === "tile" ? prompt.highlight.coord : null;
  const rect = useTileScreenRect({
    coord: tileCoord,
    boardWidth: TUTORIAL_MAP.width,
    boardHeight: TUTORIAL_MAP.height,
  });

  return (
    <>
      {rect ? <TileHighlight rect={rect} /> : null}
      <TutorialCoachPanel
        prompt={prompt}
        isFinal={isFinal}
        bottomDockRef={bottomDockRef}
        onRestart={onRestart}
        onDismiss={onDismiss}
      />
    </>
  );
}
